import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { stageMeta, stageLabel } from "@/lib/constants";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { ClipboardList, Scale, Droplets, Boxes, TrendingUp, Euro, ShoppingBag, Wine } from "lucide-react";

export default function Analytics() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <DashboardLayout><div className="py-20 text-center text-muted-foreground">{t("loading")}</div></DashboardLayout>;

  const cards = [
    { label: "stat_active", value: data.totals.orders, icon: ClipboardList, color: "#3B82F6" },
    { label: "total_processed", value: `${data.totals.weight} kg`, icon: Scale, color: "#F59E0B" },
    { label: "oil_produced", value: `${data.totals.yield} L`, icon: Droplets, color: "#3D522B" },
    { label: "stat_crates", value: data.totals.crates, icon: Boxes, color: "#8B5CF6" },
  ];

  const stageData = (data.stages || []).map((s, i) => ({ name: stageLabel(t, s), value: data.stage_counts[s.id], color: stageMeta(s.id, i).color }));
  const fmtDate = (d) => d.slice(5);
  const hasSeries = data.daily.length > 0;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-6" data-testid="analytics-page">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("analytics_title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("analytics_sub")}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.label} data-testid={`analytics-stat-${c.label}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${c.color}1a`, color: c.color }}><c.icon className="h-[18px] w-[18px]" /></div>
              <p className="mt-3 font-heading text-2xl font-bold">{c.value}</p>
              <p className="text-xs text-muted-foreground">{t(c.label)}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><TrendingUp className="h-5 w-5 text-olive" /> {t("weight_trend")}</h2>
              <span className="text-xs text-muted-foreground">{t("last_14_days")}</span>
            </div>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.daily} margin={{ left: -20, right: 8 }}>
                  <defs>
                    <linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3D522B" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3D522B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                  <Area type="monotone" dataKey="weight" stroke="#3D522B" strokeWidth={2.5} fill="url(#wg)" name={t("kg_short")} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Empty t={t} />}
          </div>

          <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold">{t("by_stage")}</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stageData} margin={{ left: -20, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" hide />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {stageData.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold">{t("orders_trend")}</h2>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.daily} margin={{ left: -20, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="orders" fill="#D4A373" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty t={t} />}
          </div>

          <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold">{t("top_clients")}</h2>
            <div className="space-y-3">
              {data.top_clients.length === 0 && <Empty t={t} />}
              {data.top_clients.map((c, i) => {
                const max = data.top_clients[0]?.weight || 1;
                return (
                  <div key={c.name} data-testid={`top-client-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium">{c.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{c.weight} kg</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${(c.weight / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {user?.modules?.store && data.sales && (
          <div className="rounded-xl border border-border bg-card p-5" data-testid="store-sales">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><ShoppingBag className="h-5 w-5 text-olive" /> {t("store_sales")}</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-olive-light p-4" data-testid="sales-revenue">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Euro className="h-[18px] w-[18px]" /></div>
                <p className="mt-3 font-heading text-2xl font-bold">€{data.sales.revenue}</p>
                <p className="text-xs text-muted-foreground">{t("revenue")}</p>
              </div>
              <div className="rounded-xl bg-secondary/60 p-4" data-testid="sales-orders">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber text-olive"><ShoppingBag className="h-[18px] w-[18px]" /></div>
                <p className="mt-3 font-heading text-2xl font-bold">{data.sales.paid_orders}</p>
                <p className="text-xs text-muted-foreground">{t("paid_orders")}</p>
              </div>
              <div className="rounded-xl bg-secondary/60 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("best_sellers")}</p>
                {data.sales.best_sellers.length ? data.sales.best_sellers.map((b, i) => (
                  <div key={i} data-testid={`best-seller-${i}`} className="flex items-center justify-between py-1 text-sm">
                    <span className="flex items-center gap-1.5"><Wine className="h-3.5 w-3.5 text-amber-deep" /> {b.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{b.qty} {t("sold")}</span>
                  </div>
                )) : <p className="text-sm text-muted-foreground">{t("no_data")}</p>}
              </div>
            </div>
            {data.sales.daily?.length > 0 && (
              <div className="mt-5">
                <h3 className="mb-3 text-sm font-semibold text-muted-foreground">{t("revenue_trend")}</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.sales.daily} margin={{ left: -20, right: 8 }}>
                    <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4A373" stopOpacity={0.4} /><stop offset="100%" stopColor="#D4A373" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#C08552" strokeWidth={2.5} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Empty({ t }) {
  return <div className="flex h-full items-center justify-center py-10 text-sm text-muted-foreground">{t("no_data")}</div>;
}
